import { useRef, useState } from 'react';
import { ArrowSmallDown, ArrowSmallLeft, ArrowSmallRight, ArrowSmallUp, Reset, ZoomIn, ZoomOut } from '@sas/react-svg';
import cx from 'classnames';
import { createUseHtmlId } from '../../../hooks/useHtmlId';
import { useResizeObserverRef } from '../../../hooks/useResizeObserver';
import { useIsRTL } from '../../../locale';
import { useNCTextLocalizer } from '../../../locale/useNCTextLocalizer';
import { renderInlineCssVars, useDesignTokens } from '../../../theme';
import { postfixTestId } from '../../../util/testId';
import { Button, ButtonVariant } from '../../Button';
import { Dialog } from '../../Dialog';
import { ToolbarSpacer } from '../../Toolbar/__internal__';
import { InnerToolbar } from '../../Toolbar/__internal__/InnerToolbar/InnerToolbar';
import { Cropper } from './Cropper';
import cn from './CropperDialog.css.json';
import './CropperDialog.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MOVE_OFFEST = 5;
const ZOOM_OFFEST = 0.1;
const useCropDialogId = createUseHtmlId('CropDialog');

/**
 * @internal
 */
export const CropperDialog = props => {
  const isRTL = useIsRTL();
  const localize = useNCTextLocalizer('ImageSelector');
  const descriptionId = useCropDialogId();
  const cropperRef = useRef(null);
  const isCropperReadyRef = useRef(false);
  const [cropperKey, setCropperKey] = useState(Date.now());
  const onMoveCropBox = type => {
    const cropper = cropperRef.current;
    if (!cropper) return;
    const {
      top,
      left,
      height,
      width
    } = cropper.getCropBoxData();
    const moveCanvas = (xOffset, yOffset) => {
      cropper.move(xOffset, yOffset);
    };
    const moveCropBox = (xOffset, yOffset) => {
      const cropBoxData = cropper.getCropBoxData();
      cropBoxData.top += yOffset;
      cropBoxData.left += xOffset;
      cropper.setCropBoxData(cropBoxData);
    };
    switch (type) {
      case 'up':
        if (top <= 0) {
          moveCanvas(0, MOVE_OFFEST);
        } else {
          moveCropBox(0, -MOVE_OFFEST);
        }
        break;
      case 'down':
        if (top + height >= cropper.getContainerData().height) {
          moveCanvas(0, -MOVE_OFFEST);
        } else {
          moveCropBox(0, MOVE_OFFEST);
        }
        break;
      case isRTL ? 'right' : 'left':
        if (left <= 0) {
          moveCanvas(MOVE_OFFEST, 0);
        } else {
          moveCropBox(-MOVE_OFFEST, 0);
        }
        break;
      case isRTL ? 'left' : 'right':
        if (left + width >= cropper.getContainerData().width) {
          moveCanvas(-MOVE_OFFEST, 0);
        } else {
          moveCropBox(MOVE_OFFEST, 0);
        }
        break;
    }
  };
  const onReset = () => {
    const cropper = cropperRef.current;
    if (!cropper || !isCropperReadyRef.current) return;
    cropper.reset();

    // Fill the crop box to the maximum size of the crop area and
    // position in the center both vertically and horizontally.
    const imageData = cropper.getImageData();
    const containerData = cropper.getContainerData();
    cropper.setCropBoxData({
      width: imageData.width,
      height: imageData.height
    });
    const cropBoxData = cropper.getCropBoxData();
    cropper.setCropBoxData({
      ...cropBoxData,
      left: (containerData.width - cropBoxData.width) / 2,
      top: (containerData.height - cropBoxData.height) / 2
    });
  };
  const [setDialogContent] = useResizeObserverRef(() => {
    // Force re-render the Cropper and the preview image
    setCropperKey(Date.now());
  });
  const dt = useDesignTokens();
  const style = renderInlineCssVars({
    'row-gap': dt.tokens['space-base-s'],
    'column-gap': dt.tokens['space-base-m']
  }, 'cropper-dialog');
  return /*#__PURE__*/_jsx(Dialog, {
    testId: props.testId,
    isOpen: props.isOpen,
    title: localize('cropDialog.title'),
    size: {
      maxWidth: 'calc(100% - 4rem)',
      maxHeight: 'calc(100% - 4rem)',
      width: 'calc(100% - 30rem)',
      height: 'calc(100% - 10rem)',
      minWidth: '45rem',
      minHeight: '30rem'
    },
    firstAction: {
      text: localize('cropDialog.confirmButton.label'),
      onPress: () => {
        if (!cropperRef.current) return;
        props.onCrop?.(cropperRef.current.getCroppedCanvas());
      },
      initialFocus: true,
      isDefault: true
    },
    dismissAction: {
      text: localize('cropDialog.dismissButton.label'),
      onPress: props.onDismiss
    },
    style: style,
    ariaDescribedBy: descriptionId,
    busyStateIndicatorProps: {
      active: props.isBusy,
      delay: 0
    },
    children: /*#__PURE__*/_jsxs("div", {
      className: cn.dialogContent,
      ref: setDialogContent,
      children: [/*#__PURE__*/_jsx(Cropper, {
        cropperKey: cropperKey,
        src: props.src,
        cropperRef: cropperRef,
        ellipse: props.ellipse,
        aspectRatio: props.aspectRatio,
        previewClassName: cn.preview,
        onReady: () => {
          isCropperReadyRef.current = true;
          onReset();
        },
        onError: props.onError,
        className: cn.cropper
      }), /*#__PURE__*/_jsxs("div", {
        className: cn.bar,
        children: [/*#__PURE__*/_jsx(Button, {
          testId: postfixTestId(props.testId, 'zoomInButton'),
          variant: ButtonVariant.Icon,
          icon: {
            icon: ZoomIn,
            tooltip: localize('cropDialog.zoomInButton.tooltip.label'),
            ariaLabel: localize('cropDialog.zoomInButton.aria')
          },
          onPress: () => cropperRef.current?.zoom(ZOOM_OFFEST)
        }), /*#__PURE__*/_jsx(Button, {
          testId: postfixTestId(props.testId, 'zoomOutButton'),
          variant: ButtonVariant.Icon,
          icon: {
            icon: ZoomOut,
            tooltip: localize('cropDialog.zoomOutButton.tooltip.label'),
            ariaLabel: localize('cropDialog.zoomOutButton.aria')
          },
          onPress: () => cropperRef.current?.zoom(-ZOOM_OFFEST)
        }), /*#__PURE__*/_jsx(ToolbarSpacer, {}), /*#__PURE__*/_jsx(InnerToolbar, {
          items: [{
            type: 'Button',
            testId: postfixTestId(props.testId, 'moveLeftButton'),
            variant: ButtonVariant.Icon,
            icon: {
              icon: ArrowSmallLeft,
              tooltip: localize('cropDialog.moveLeftButton.tooltip.label'),
              ariaLabel: localize('cropDialog.moveLeftButton.aria')
            },
            onPress: () => onMoveCropBox('left'),
            enableContinuousPress: true
          }, {
            type: 'Button',
            testId: postfixTestId(props.testId, 'moveRightButton'),
            variant: ButtonVariant.Icon,
            icon: {
              icon: ArrowSmallRight,
              tooltip: localize('cropDialog.moveRightButton.tooltip.label'),
              ariaLabel: localize('cropDialog.moveRightButton.aria')
            },
            onPress: () => onMoveCropBox('right'),
            enableContinuousPress: true
          }, {
            type: 'Button',
            testId: postfixTestId(props.testId, 'moveUpButton'),
            variant: ButtonVariant.Icon,
            icon: {
              icon: ArrowSmallUp,
              tooltip: localize('cropDialog.moveUpButton.tooltip.label'),
              ariaLabel: localize('cropDialog.moveUpButton.aria')
            },
            onPress: () => onMoveCropBox('up'),
            enableContinuousPress: true
          }, {
            type: 'Button',
            testId: postfixTestId(props.testId, 'moveDownButton'),
            variant: ButtonVariant.Icon,
            icon: {
              icon: ArrowSmallDown,
              tooltip: localize('cropDialog.moveDownButton.tooltip.label'),
              ariaLabel: localize('cropDialog.moveDownButton.aria')
            },
            onPress: () => onMoveCropBox('down'),
            enableContinuousPress: true
          }]
        }), /*#__PURE__*/_jsx(ToolbarSpacer, {}), /*#__PURE__*/_jsx(Button, {
          testId: postfixTestId(props.testId, 'resetButton'),
          icon: {
            icon: Reset,
            tooltip: localize('cropDialog.resetButton.tooltip.label'),
            ariaLabel: localize('cropDialog.resetButton.aria')
          },
          onPress: onReset,
          variant: ButtonVariant.Icon
        })]
      }), /*#__PURE__*/_jsx("div", {
        className: cn.description,
        id: descriptionId,
        children: localize('cropDialog.desc')
      }), /*#__PURE__*/_jsx("div", {
        className: cx(cn.preview, props.ellipse && cn.ellipse)
      }, cropperKey)]
    })
  });
};
//# sourceMappingURL=CropperDialog.js.map